import { UntitledIcon } from '@faceup/icons'
import { ulLinkExternal01 } from '@faceup/icons/ulLinkExternal01'
import { Typography } from '@faceup/ui-base'
import { getRootDomain } from '@faceup/utils'
import DOMPurify from 'dompurify'
import parse, { attributesToProps, type DOMNode, domToReact, type Element } from 'html-react-parser'
import type { ComponentProps } from 'react'
import { useLinkExternalConfirm } from '../hooks/useLinkExternalConfirm'
import { WysiwygBlockquote } from './WysiwygBlockquote'

type HtmlRendererProps = { html: string }

const isTag = (node: DOMNode): node is Element => node.type === 'tag'

/**
 * Renders HTML with some sanitization and a hook to make all links open a new window
 * TL;DR:
 *  - Sanitize the HTML with DOMPurify and add a hook to make all links open a new window
 *  - Parse the HTML with html-react-parser and add confirm alert for all links
 *  - Render the HTML
 */
export const HtmlRenderer = ({ html }: HtmlRendererProps) => {
  const linkExternalConfirm = useLinkExternalConfirm()
  // https://github.com/cure53/DOMPurify/blob/main/demos/hooks-target-blank-demo.html
  // Add a hook to make all links open a new window
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    // set all elements owning target to target=_blank
    if ('target' in node) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  })

  const renderAnchor = (domNode: Element) => {
    const props = attributesToProps(domNode.attribs)
    return (
      <a
        {...props}
        onClick={e => {
          if (
            typeof props['href'] === 'string' &&
            getRootDomain(props['href']) !== getRootDomain(window.location.href)
          ) {
            e.preventDefault()
            linkExternalConfirm(props['href'])
          }
        }}
      >
        {domToReact(domNode.children as DOMNode[], options)}{' '}
        <UntitledIcon icon={ulLinkExternal01} />
      </a>
    )
  }

  const renderParagraph = (domNode: Element) => {
    const props = attributesToProps(domNode.attribs)
    return (
      <Typography.Paragraph {...props} className='mb-0 pb-[1em]'>
        {domToReact(domNode.children as DOMNode[], options)}
      </Typography.Paragraph>
    )
  }

  const renderHeading = (domNode: Element) => {
    const props = attributesToProps(domNode.attribs)
    return (
      <Typography.Title
        className='pb-2'
        {...props}
        level={Number(domNode.name.at(1)) as ComponentProps<typeof Typography.Title>['level']}
      >
        {domToReact(domNode.children as DOMNode[], options)}
      </Typography.Title>
    )
  }

  const renderBlockquote = (domNode: Element) => {
    const children = domToReact(domNode.children as DOMNode[], options)
    if (typeof children === 'string' || Array.isArray(children)) {
      return <WysiwygBlockquote>{children}</WysiwygBlockquote>
    }

    /**
     * The wysiwyg for some reasons injects <p> into a <blockquote> tag.
     * To avoid this, we need to get the children of the children.
     */
    return <WysiwygBlockquote>{children.props.children}</WysiwygBlockquote>
  }

  const redirectTarget = html.match(/^<div data-redirect="([^"]+)"><\/div>$/)?.[1]
  if (redirectTarget) {
    // Only allow same-origin relative paths to prevent open-redirect / unsafe-scheme attacks.
    if (redirectTarget.startsWith('/') && !redirectTarget.startsWith('//')) {
      window.location.assign(redirectTarget)
    }
    return null
  }

  const cleanedHtml = DOMPurify.sanitize(html)
  const options = {
    replace: (domNode: DOMNode) => {
      if (!isTag(domNode)) {
        return domNode
      }

      switch (domNode.name) {
        case 'a':
          return renderAnchor(domNode)
        case 'h1':
        case 'h2':
        case 'h3':
        case 'h4':
        case 'h5':
        case 'h6':
          return renderHeading(domNode)
        case 'p':
          return renderParagraph(domNode)
        case 'blockquote':
          return renderBlockquote(domNode)
        default:
          return domNode
      }
    },
  }
  const parsedCleanedHtml = parse(cleanedHtml, options)

  return <>{parsedCleanedHtml}</>
}
